import React, { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { LANDING } from "@/constants/testIds";
import { WEB_LOGIN_URL, WEB_REGISTER_URL } from "@/constants/site";
import { captureEvent } from "@/lib/analytics";

const LINKS = [
  { to: "/", label: "Home", testId: LANDING.navHomeLink, end: true },
  { to: "/features", label: "Features", testId: LANDING.navFeaturesLink },
  { to: "/pricing", label: "Pricing", testId: LANDING.navPricingLink },
  { to: "/about", label: "About", testId: LANDING.navAboutLink },
  { to: "/support", label: "Support", testId: LANDING.navSupportLink },
  { to: "/contact", label: "Contact", testId: LANDING.navContactLink },
];

const linkClass = ({ isActive }) =>
  `text-sm font-medium transition-colors ${
    isActive ? "text-[#0E6B6B]" : "text-[#2c4040] hover:text-[#0E6B6B]"
  }`;

export const Navbar = () => {
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 w-full backdrop-blur-md bg-[#F7FAF9]/85 border-b border-teal-900/5">
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:h-20 sm:px-6 lg:px-12">
        <Link to="/" data-testid={LANDING.navLogo} aria-label="Shopbook home">
          <Logo />
        </Link>

        <div className="hidden md:flex items-center gap-7">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.testId}
              className={linkClass}
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <a href={WEB_LOGIN_URL} target="_blank" rel="noreferrer" onClick={() => captureEvent("conversion_cta_clicked", { placement: "navbar", destination: "web_login" })} className="px-2 py-2.5 text-sm font-semibold text-[#2c4040] hover:text-[#0E6B6B]">
            Sign in
          </a>
          <a
            href={WEB_REGISTER_URL}
            target="_blank"
            rel="noreferrer"
            data-testid={LANDING.navGetStarted}
            onClick={() => captureEvent("conversion_cta_clicked", { placement: "navbar", destination: "web_register" })}
            className="inline-flex items-center rounded-full bg-[#0E6B6B] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#138282] transition-colors"
          >
            Start free
          </a>
        </div>

        <button
          aria-label="Toggle menu"
          aria-expanded={open}
          aria-controls="mobile-navigation"
          data-testid={LANDING.navMobileToggle}
          className="-mr-2 rounded-xl p-2.5 text-[#173B3B] transition-colors hover:bg-teal-900/5"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      {open && (
        <div id="mobile-navigation" className="md:hidden border-t border-teal-900/5 bg-white">
          <div className="flex flex-col gap-4 px-5 py-5 sm:px-6">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={() => setOpen(false)}
                data-testid={`${l.testId}-mobile`}
                className={linkClass}
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={WEB_LOGIN_URL}
              target="_blank"
              rel="noreferrer"
              onClick={() => {
                setOpen(false);
                captureEvent("conversion_cta_clicked", { placement: "mobile_navbar", destination: "web_login" });
              }}
              className="text-sm font-medium text-[#2c4040]"
            >
              Sign in to web app
            </a>
            <a
              href={WEB_REGISTER_URL}
              target="_blank"
              rel="noreferrer"
              onClick={() => {
                setOpen(false);
                captureEvent("conversion_cta_clicked", { placement: "mobile_navbar", destination: "web_register" });
              }}
              data-testid={`${LANDING.navGetStarted}-mobile`}
              className="inline-flex items-center justify-center rounded-full bg-[#0E6B6B] px-5 py-3 text-sm font-semibold text-white"
            >
              Start free
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
